/* @jsx h */
import { defineComponent, h, computed } from "vue";
import {
  DateRangePicker,
  Dropdown,
  type DateRange,
  useLocalization,
} from "@lemmonhq/uikit";
import {
  declaredControls,
  endDateFromBounds,
  timeFromBound,
  toReportBounds,
  FULL_DAY_WINDOW,
  type CatalogEntry,
  type ReportTimeWindow,
} from "../state";

export type FilterOptions = {
  staff: Array<{ id: number; name: string }>;
  paymentMethods: string[];
  serviceMethods: string[];
};

/** Renders only the controls declared by the active report's schema. */
export const ReportFilters = defineComponent({
  name: "DataRoomReportFilters",
  props: {
    entry: { type: Object, required: true },
    parameters: { type: Object, required: true },
    options: { type: Object, default: null },
    timeWindow: { type: Object, default: null },
    range: { type: Object, default: null },
    onChange: { type: Function, required: true },
    onRangeChange: { type: Function, default: null },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const controls = computed(() =>
      declaredControls(props.entry as CatalogEntry),
    );
    const params = computed(() => props.parameters as Record<string, unknown>);
    const options = computed<FilterOptions>(
      () =>
        props.options ?? {
          staff: [],
          paymentMethods: [],
          serviceMethods: [],
        },
    );

    // No `options` prop passed to DateRangePicker below — it falls back to its
    // own full preset list (today, yesterday, this/last week, this/last month,
    // this/last quarter, this/last year, last 12 months) from @lemmonhq/blogic,
    // instead of the narrower subset this report used to hardcode.
    // Falls back to the whole day when the restaurant has no opening hours,
    // which keeps this identical to the pre-time behaviour.
    const timeWindow = computed<ReportTimeWindow>(
      () => (props.timeWindow as ReportTimeWindow | null) ?? FULL_DAY_WINDOW,
    );

    // The tab's own `from`/`to` stay the source of truth for what is displayed
    // — a deep link carries its own window and must not be overridden by the
    // shared range. The shared range only supplies the preset *type*, which
    // timestamps cannot express: without it a report opened on "this month"
    // would read back as a frozen custom range.
    const currentRange = computed<DateRange>(() => {
      const startDate = String(params.value.from ?? "").slice(0, 10);
      // Not the `to` bound's own day: a window spanning midnight closes on the
      // morning after the last day the user picked.
      const endDate = endDateFromBounds(params.value.from, params.value.to);
      const shared = props.range as DateRange | null;
      const type =
        shared && shared.startDate === startDate && shared.endDate === endDate
          ? shared.type
          : "custom";
      return {
        type,
        startDate,
        endDate,
        startTime: timeFromBound(params.value.from, timeWindow.value.startTime),
        endTime: timeFromBound(params.value.to, timeWindow.value.endTime),
      };
    });

    const emit = (patch: Record<string, unknown>) => props.onChange(patch);

    const onRange = (range: DateRange | undefined) => {
      if (!range) return;
      const window = {
        startTime: range.startTime ?? timeWindow.value.startTime,
        endTime: range.endTime ?? timeWindow.value.endTime,
      };
      // The full range (preset type included) goes to the shared value; the
      // report parameters only ever carry the resolved timestamps.
      props.onRangeChange?.({ ...range, ...window });
      emit(toReportBounds(range.startDate, range.endDate, window));
    };

    const toggleInArray = (key: string, value: string | number) => {
      const current = Array.isArray(params.value[key])
        ? [...(params.value[key] as Array<string | number>)]
        : [];
      const idx = current.findIndex((v) => v === value);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(value);
      emit({ [key]: current });
    };

    const multiSelect = (opts: {
      key: string;
      label: string;
      icon: string;
      items: Array<{ value: string | number; label: string }>;
    }) => {
      const selected = Array.isArray(params.value[opts.key])
        ? (params.value[opts.key] as Array<string | number>)
        : [];
      return (
        <Dropdown
          placement="bottom-start"
          contentClass="mt-1.5 menu p-2 shadow-xl bg-base-100 rounded-2xl w-56 max-h-72 overflow-y-auto flex-nowrap"
        >
          {{
            trigger: () => (
              <button
                type="button"
                class={`btn btn-sm gap-1.5 normal-case rounded-xl font-medium ${
                  selected.length > 0
                    ? "btn-primary btn-outline"
                    : "bg-base-100 text-base-content/70 hover:border-primary/40 hover:text-accent"
                }`}
              >
                <span class="material-symbols-outlined text-base">
                  {opts.icon}
                </span>
                {opts.label}
                {selected.length > 0 && (
                  <span class="badge badge-primary badge-sm">
                    {selected.length}
                  </span>
                )}
              </button>
            ),
            default: () =>
              opts.items.length === 0 ? (
                <span class="text-xs text-base-content/70 px-2 py-1">
                  {t("reports.dataroom.filter_no_options")}
                </span>
              ) : (
                opts.items.map((it) => (
                  <label
                    key={String(it.value)}
                    class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-base-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-primary"
                      checked={selected.some((v) => v === it.value)}
                      onChange={() => toggleInArray(opts.key, it.value)}
                    />
                    <span class="text-sm">{it.label}</span>
                  </label>
                ))
              ),
          }}
        </Dropdown>
      );
    };

    return () => (
      <div class="flex flex-wrap items-center gap-2">
        {controls.value.has("date-range") && (
          <DateRangePicker
            showTime={true}
            value={currentRange.value}
            onChange={onRange}
          />
        )}
        {controls.value.has("payment-methods") &&
          multiSelect({
            key: "paymentMethods",
            label: t("reports.dataroom.filter_payment"),
            icon: "credit_card",
            items: options.value.paymentMethods.map((m) => ({
              value: m,
              label: m,
            })),
          })}
        {controls.value.has("staff") &&
          multiSelect({
            key: "userIds",
            label: t("reports.dataroom.filter_staff"),
            icon: "person",
            items: options.value.staff.map((s) => ({
              value: s.id,
              label: s.name,
            })),
          })}
        {controls.value.has("service-methods") &&
          multiSelect({
            key: "serviceMethods",
            label: t("reports.dataroom.filter_service"),
            icon: "restaurant",
            items: options.value.serviceMethods.map((m) => ({
              value: m,
              label: m,
            })),
          })}
      </div>
    );
  },
});
